import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Calendar, User } from 'lucide-react';
import { publicAPI } from '../services/apiService';
import { createSlug } from '../utils/slugify';
import { getImageUrl } from '../utils/imageUtils';

const Blog = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [blogPosts, setBlogPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await publicAPI.getBlog();
      setBlogPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20">
        <div className="absolute inset-0 bg-black opacity-40 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/reserve/LJIZlzHgQ7WPSh5KVTCB_Typewriter.jpg?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxtaW5pbWFsaXN0fGVufDB8fHxibGFja19hbmRfd2hpdGV8MTc1NzUwNzM2OHww&ixlib=rb-4.1.0&q=85')`
          }}
        ></div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 
            className="text-5xl md:text-6xl font-bold mb-6"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {t('blog.title')}
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            {t('blog.subtitle')}
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {blogPosts.map((post, index) => (
              <article 
                key={post.id} 
                className={`group ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} flex flex-col lg:flex gap-8 items-center bg-gray-900 overflow-hidden hover:bg-gray-800 transition-all duration-500`}
              >
                {/* Image */}
                <div className="lg:w-1/2 overflow-hidden">
                  <img 
                    src={getImageUrl(post.image)} 
                    alt={currentLang === 'ru' ? post.title_ru : post.title_de}
                    className="w-full h-64 lg:h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                
                {/* Content */}
                <div className="lg:w-1/2 p-8 lg:p-12">
                  <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(post.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span>{post.author}</span>
                    </div>
                  </div>
                  
                  <h2 
                    className="text-3xl lg:text-4xl font-bold mb-4 group-hover:text-gray-300 transition-colors duration-300"
                    style={{ fontFamily: '"Times New Roman", serif' }}
                  >
                    {currentLang === 'ru' ? post.title_ru : post.title_de}
                  </h2>
                  
                  <p className="text-gray-400 text-lg leading-relaxed mb-6">
                    {currentLang === 'ru' ? post.excerpt_ru : post.excerpt_de}
                  </p>
                  
                  <Link 
                    to={`/blog/${createSlug(post.title_de)}-${post.id}`}
                    className="inline-flex items-center text-white hover:text-gray-300 transition-colors duration-300 font-medium"
                  >
                    {t('blog.readMore')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>


        </div>
      </section>
    </div>
  );
};

export default Blog;